/** Numbered card whose title toggles the body (▾ open, ▸ closed), like `Section` in the desktop app (ui/widgets.py). */
import { createContext, useContext, useId, useState, type ReactNode } from "react";

interface Props {
  title: string;
  defaultOpen?: boolean;
  /** Always visible next to the title (a summary of the section's choice). */
  aside?: ReactNode;
  children: ReactNode;
}

/** Whether the section around a component is open (true outside any section): the editor's "Cắt & xoay" shows its
 * frame on the picture only while its section is open. */
const SectionOpen = createContext(true);
export const useSectionOpen = () => useContext(SectionOpen);

export function Section({ title, defaultOpen = true, aside, children }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <section className={`section${open ? " is-open" : ""}`}>
      <h2 className="section-head">
        <button type="button" className="section-toggle" aria-expanded={open} aria-controls={bodyId}
                onClick={() => setOpen((v) => !v)}>
          <span className="chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
          {title}
        </button>
        {aside && <span className="section-aside">{aside}</span>}
      </h2>
      {/* kept mounted while closed: the file input and the choices live on */}
      <div id={bodyId} className="section-body" hidden={!open}>
        <SectionOpen.Provider value={open}>{children}</SectionOpen.Provider>
      </div>
    </section>
  );
}
